<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：クロッパー
 *
 * 作成者：2022/01/27 ノア）虞
 * 最終修正者：2022/01/27 ノア）虞
 *
 -->
<template>
  <div>
    <ttiDialog
      :isShow="isShowSelf"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('imageCropper.title')"
      width="824px"
      marginTop="10vh"
    >
      <template slot="body">
        <div class="container">
          <div class="cropper-body">
            <vueCropper
              ref="cropperRef"
              :img="cropperImgSelf"
              :outputType="'jpeg'"
              :autoCrop="true"
              :autoCropWidth="cropWidth"
              :autoCropHeight="cropHeight"
              @imgLoad="onImgLoad"
            ></vueCropper>
          </div>
          <p class="cropper-p">{{ $t('imageCropper.imageTypeText') }}</p>
          <p class="cropper-p">{{ $t('imageCropper.imageStyleText') }}</p>
          <p class="cropper-p">{{ $t('imageCropper.imageSizeText') }}</p>
        </div>
      </template>
      <template slot="foot">
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSelectButtonClick"
          v-tooltip="{
            content: `${$t('imageCropper.selectTip')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
          >{{ $t('buttons.select') }}</el-button
        >
        <input
          class="display-none"
          type="file"
          id="selectImg"
          ref="selectImgRef"
          accept="image/png, image/jpeg, image/jpg"
          @change="onUploadImg"
        />
        <el-button
          class="tti-btn-default tti-btn-001"
          :disabled="!cropperImgSelf"
          @click="onOkButtonClick"
          v-tooltip="{
            content: `${$t('imageCropper.okTip')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
          >{{ $t('buttons.ok') }}</el-button
        >
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { VueCropper } from 'vue-cropper'
import { Component, Vue, Prop, Ref, Emit, Watch } from 'vue-property-decorator'

import manualConfig from '@/config/manualConfig'
import TtiDialog from '@/components/TtiDialog.vue'
import { AllowedImgType, MaxImgSize, MinImage } from '@/commons/constants'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { i18n } from '@/locales'
import { uploadPlaceImg, uploadPlaceDtlImg } from '@/store/apis'
import { ImagePathData, ResponseData } from '@/models'
import { UploadApiType } from '@/commons/constants'
import HelperService from '@/service/helperService'
import { AxiosResponse } from 'axios'
import { changeBlobToJson } from '@/store/apis/apiClient'
import { common } from '@/store/modules'

/**
 * ドラッグツリーコンポーネント
 *
 * @class
 */
@Component({
  name: 'cropper',
  components: {
    VueCropper,
    TtiDialog
  }
})
export default class Cropper extends Vue {
  /**
   * 画像UPLOADのref
   */
  @Ref() readonly selectImgRef!: HTMLInputElement

  /**
   * cropperのref
   */
  // eslint-disable-next-line
  @Ref() readonly cropperRef!: any

  /**
   * 営業所ID
   */
  @Prop({
    default: undefined
  })
  businessOfficeId!: string

  /**
   * 入力画像
   */
  @Prop({
    default: ''
  })
  cropperImg!: string

  /**
   * 画像アップロードAPIタイプ
   */
  @Prop({
    default: UploadApiType.placeImg
  })
  uploadApiName!: string

  /**
   * 入力画像
   *
   */
  cropperImgSelf = ''

  /**
   * スクリーンショットボックスの幅
   */
  cropWidth = ''
  /**
   * スクリーンショットボックスの高さ
   */
  cropHeight = ''

  /**
   * アップロードする画像の名前
   */
  toUploadFileName = ''

  /**
   * アップロードする画像の種類
   */
  toUploadFileType = ''

  /**
   * 出力画像
   */
  outImagePath: ImagePathData = {
    /**
     * 画像パス
     */
    imagePath: '',
    /**
     * サムネイル画像のパス
     */
    iconPath: ''
  }

  /**
   * 画像選択サブ画面のマニュアルキーを取得
   *
   * @returns {string} 画像選択サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.cropper
  }

  /**
   * ダイアログボックスを表示するかどうかのフラグ
   */
  @Prop({
    default: false
  })
  isShow!: boolean

  /**
   * 表示状態変更後の関数
   *
   * @param {boolean} status 表示状態
   * @returns {boolean} 表示状態
   */
  @Emit('handelDisplay')
  handelDisplay(status: boolean): boolean {
    return status
  }

  /**
   * 画像が正常にアップロードされた後の関数
   *
   * @returns {string} 画像パス
   */
  @Emit('handelImgPath')
  // eslint-disable-next-line
  handelImgPath(): any {
    return this.outImagePath
  }

  /**
   * ダイアログボックスを表示するかどうかを取得する（画面用）
   *
   * @returns {boolean} ダイアログボックスを表示するかどうか（画面用）
   */
  get isShowSelf(): boolean {
    return this.isShow
  }

  /**
   * ダイアログボックスを表示するかどうかを設定する（画面用）
   */
  set isShowSelf(value: boolean) {
    this.handelDisplay(value)
  }

  @Watch('cropperImg', { immediate: true })
  isCropperImgChange(value: string): void {
    this.cropperImgSelf = value

    this.toUploadFileName = HelperService.getImageNameFromLink(value)
  }

  /**
   * 選択ボタンを押した後の処理
   *
   * @returns {void}
   */
  onSelectButtonClick(): void {
    if (this.selectImgRef) {
      this.selectImgRef.value = ''
    }
    this.selectImgRef.click()
  }

  /**
   * 確認ボタンを押した後の処理
   *
   * @returns {void}
   */
  onOkButtonClick(): void {
    const formData = new FormData()

    this.cropperRef.getCropBlob((data: Blob) => {
      const fileData = new File([data], this.toUploadFileName, { type: this.toUploadFileType })

      if (this.businessOfficeId) {
        formData.append('businessOfficeId', this.businessOfficeId)
      }

      if (fileData) {
        formData.append('data', fileData)
      }

      this.doUploadImg(formData)
    })
  }

  /**
   * 画像アップロード
   *
   * @async
   * @param {FormData} formData パラメータ
   * @returns {Promise<void>}
   */
  async doUploadImg(formData: FormData): Promise<void> {
    try {
      let res
      if (this.uploadApiName === UploadApiType.placeImg) {
        res = await uploadPlaceImg(formData)
      } else if (this.uploadApiName === UploadApiType.placeDtlImg) {
        res = await uploadPlaceDtlImg(formData)
      }
      if (!res) return
      this.outImagePath.imagePath = res.imagePath
      this.outImagePath.iconPath = res.iconPath

      this.handelImgPath()
      this.handelDisplay(false)
    } catch (e) {
      this.$log.debug('Cropper > saveTableDataAPI > error', e)
      const err = e as AxiosResponse<ResponseData>
      // 管理対象名の情報を変更できませんの場合、営業所割当情報を再取得する。
      changeBlobToJson(err).then(commonResponse => {
        if (commonResponse.common.code === 'E00093') {
          common.setBussinessOfficeStatus(true)
          this.onHandelDisplay(false)
        }
      })
      throw e
    }
  }

  /**
   * アップロードされた画像の後処理
   *
   * @param {any} event イベント
   */
  // eslint-disable-next-line
  onUploadImg(event: any): void {
    const file = event.target.files[0]

    if (!this.correlationCheck(file)) return

    const reader = new FileReader()
    // eslint-disable-next-line
    reader.onload = (e: any) => {
      const image = new Image()
      let data

      if (typeof e.target.result === 'object') {
        image.src = window.URL.createObjectURL(new Blob([e.target.result]))
      } else {
        image.src = e.target.result
      }

      // eslint-disable-next-line
      image.onload = () => {
        const imgWidth = image.width
        const imgHeight = image.height

        if (!this.correlationWhCheck(imgWidth, imgHeight)) return

        data = image.src

        this.changeCrop(imgWidth, imgHeight)

        this.cropperImgSelf = data

        this.selectImgRef.value = ''

        this.toUploadFileName = file.name
        this.toUploadFileType = file.type
      }
    }

    reader.readAsArrayBuffer(file)
  }

  /**
   * 相関チェックを行う
   *
   * @param {number} imgWidth 幅
   * @param {number} imgHeight 高
   * @returns {boolean} 相関チェック結果
   */
  correlationWhCheck(imgWidth: number, imgHeight: number): boolean {
    if (imgWidth < MinImage.Width && imgHeight < MinImage.Height) {
      msgBoxService.info(i18n.t('imageCropper.imgStyleMsg').toString())
      return false
    }

    return true
  }

  /**
   * 相関チェックを行う
   *
   * @param {File} imageFile 画像
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(imageFile: File): boolean {
    const fileType = imageFile.type

    if (!AllowedImgType.includes(fileType)) {
      msgBoxService.info(i18n.t('imageCropper.imgTypeMsg').toString())
      return false
    }

    const fileSize = imageFile.size

    if (fileSize > MaxImgSize) {
      msgBoxService.info(i18n.t('imageCropper.imgSizeMsg', [2]).toString())
      return false
    }

    return true
  }

  /**
   * スクリーンショットボックスの幅と高さの変更
   *
   * @param {number} imgWidth 幅
   * @param {number} imgHeight 高さ
   * @returns {void}
   */
  changeCrop(imgWidth: number, imgHeight: number): void {
    if (imgWidth <= MinImage.Width && imgHeight <= MinImage.Height) {
      this.cropWidth = imgWidth + 'px'
      this.cropHeight = imgHeight + 'px'
    } else if (imgWidth / MinImage.Width > imgHeight / MinImage.Height) {
      this.cropWidth = MinImage.Width + 'px'
      this.cropHeight = imgHeight / (imgWidth / MinImage.Width) + 'px'
    } else {
      this.cropWidth = imgWidth / (imgHeight / MinImage.Height) + 'px'
      this.cropHeight = MinImage.Height + 'px'
    }
  }

  /**
   * 画像読み込み後の処理
   *
   * @returns {void}
   */
  onImgLoad(): void {
    this.changeCrop(this.cropperRef.trueWidth, this.cropperRef.trueHeight)
  }

  /**
   * 表示状態変更
   *
   * @param {boolean} value 表示状態
   * @returns {void}
   */
  onHandelDisplay(value: boolean): void {
    this.isShowSelf = value
  }
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  flex-direction: column;

  .cropper-body {
    width: 800px;
    height: 480px;
    margin: 0 auto 12px;
  }

  .cropper-p {
    color: #527acc;
    margin: 0 0 10px;
  }
}

.display-none {
  position: absolute;
  clip: rect(0 0 0 0);
}
</style>
